import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { Library } from '@/types/api';
import {
  useLibraries,
  useCreateLibrary,
  useUpdateLibrary,
  useDeleteLibrary,
  useLibraryStats,
} from '@/hooks/useLibraries';
import {
  useLibraryAliases,
  useCreateLibraryAlias,
  useDeleteLibraryAlias,
  useUpdateLibraryAlias,
} from '@/hooks/useLibraryAliases';
import {
  useLibrarySections,
  useCreateLibrarySection,
  useDeleteLibrarySection,
  useUpdateLibrarySection,
} from '@/hooks/useLibrarySections';
import LibraryCard from '@/components/libraries/LibraryCard';
import LibraryForm from '@/components/libraries/LibraryForm';
import AliasList from '@/components/libraries/AliasList';
import SectionEditor from '@/components/libraries/SectionEditor';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ErrorAlert from '@/components/common/ErrorAlert';
import { UpdateLibrarySectionRequest, UpdateLibraryAliasRequest } from '@/types/api';
import { useTranslation } from 'react-i18next';

const LibrariesPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const [editingLibrary, setEditingLibrary] = useState<Library | null>(null);
  const [aliasLibrary, setAliasLibrary] = useState<Library | null>(null);
  const [sectionLibrary, setSectionLibrary] = useState<Library | null>(null);

  // Fetch libraries
  const { data: libraries, isLoading, isError, error } = useLibraries();

  // Mutations
  const createLibrary = useCreateLibrary();
  const updateLibrary = useUpdateLibrary();
  const deleteLibrary = useDeleteLibrary();

  // Alias hooks (only fetch when dialog is open)
  const {
    data: aliases = [],
    isLoading: aliasesLoading,
  } = useLibraryAliases(aliasLibrary?.id || 0);
  const createAlias = useCreateLibraryAlias();
  const updateAlias = useUpdateLibraryAlias();
  const deleteAlias = useDeleteLibraryAlias();

  // Section hooks (only fetch when dialog is open)
  const {
    data: sections = [],
    isLoading: sectionsLoading,
  } = useLibrarySections(sectionLibrary?.id || 0);
  const createSection = useCreateLibrarySection();
  const updateSection = useUpdateLibrarySection();
  const deleteSection = useDeleteLibrarySection();

  const handleOpenCreateForm = () => {
    setEditingLibrary(null);
    setFormOpen(true);
  };

  const handleOpenEditForm = (library: Library) => {
    setEditingLibrary(library);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingLibrary(null);
  };

  const handleSaveLibrary = async (data: any) => {
    try {
      if (editingLibrary) {
        await updateLibrary.mutateAsync({
          id: editingLibrary.id,
          data,
        });
      } else {
        await createLibrary.mutateAsync(data);
      }
      handleCloseForm();
    } catch (error) {
      console.error('Failed to save library:', error);
    }
  };

  const handleDeleteLibrary = async (library: Library) => {
    if (
      !window.confirm(
        t('libraries.errors.confirmDelete', { name: library.canonical_name })
      )
    ) {
      return;
    }

    try {
      await deleteLibrary.mutateAsync(library.id);
    } catch (error) {
      console.error('Failed to delete library:', error);
    }
  };

  const handleManageAliases = (library: Library) => {
    setAliasLibrary(library);
  };

  const handleManageSections = (library: Library) => {
    setSectionLibrary(library);
  };

  const handleReviewLibrary = (library: Library) => {
    navigate(`/libraries/${library.id}/review`);
  };

  const handleAddAlias = async (data: any) => {
    if (!aliasLibrary) return;

    try {
      await createAlias.mutateAsync({
        libraryId: aliasLibrary.id,
        data,
      });
    } catch (error) {
      console.error('Failed to add alias:', error);
    }
  };

  const handleUpdateAlias = async (aliasId: number, data: UpdateLibraryAliasRequest) => {
    if (!aliasLibrary) return;

    try {
      await updateAlias.mutateAsync({
        libraryId: aliasLibrary.id,
        aliasId,
        data,
      });
    } catch (error) {
      console.error('Failed to update alias:', error);
    }
  };

  const handleDeleteAlias = async (aliasId: number) => {
    if (!aliasLibrary) return;

    try {
      await deleteAlias.mutateAsync({
        libraryId: aliasLibrary.id,
        aliasId,
      });
    } catch (error) {
      console.error('Failed to delete alias:', error);
    }
  };

  const handleAddSection = async (data: any) => {
    if (!sectionLibrary) return;

    try {
      await createSection.mutateAsync({
        libraryId: sectionLibrary.id,
        data,
      });
    } catch (error) {
      console.error('Failed to add section:', error);
    }
  };

  const handleUpdateSection = async (sectionId: number, data: UpdateLibrarySectionRequest) => {
    if (!sectionLibrary) return;

    try {
      await updateSection.mutateAsync({
        libraryId: sectionLibrary.id,
        sectionId,
        data,
      });
    } catch (error) {
      console.error('Failed to update section:', error);
    }
  };

  const handleDeleteSection = async (sectionId: number) => {
    if (!sectionLibrary) return;

    try {
      await deleteSection.mutateAsync({
        libraryId: sectionLibrary.id,
        sectionId,
      });
    } catch (error) {
      console.error('Failed to delete section:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('libraries.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateForm}
        >
          {t('libraries.createButton')}
        </Button>
      </Box>

      {isLoading && <LoadingSpinner message={t('libraries.loading')} />}

      {isError && <ErrorAlert error={error} title={t('libraries.errors.failedToLoad')} />}

      {createLibrary.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => createLibrary.reset()}>
          {t('libraries.errors.failedToCreate')}
        </Alert>
      )}

      {updateLibrary.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => updateLibrary.reset()}>
          {t('libraries.errors.failedToUpdate')}
        </Alert>
      )}

      {deleteLibrary.isError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => deleteLibrary.reset()}>
          {t('libraries.errors.failedToDelete')}
        </Alert>
      )}

      {/* Libraries Grid */}
      {!isLoading && libraries && libraries.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {t('libraries.emptyState.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('libraries.emptyState.subtitle')}
          </Typography>
        </Paper>
      )}

      {!isLoading && libraries && libraries.length > 0 && (
        <Grid container spacing={3}>
          {libraries.map((library) => (
            <Grid item xs={12} md={6} lg={4} key={library.id}>
              <LibraryStatsCard
                library={library}
                onEdit={handleOpenEditForm}
                onDelete={handleDeleteLibrary}
                onManageAliases={handleManageAliases}
                onManageSections={handleManageSections}
                onReview={handleReviewLibrary}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Library Form Dialog */}
      <LibraryForm
        open={formOpen}
        library={editingLibrary}
        onClose={handleCloseForm}
        onSave={handleSaveLibrary}
        isLoading={createLibrary.isPending || updateLibrary.isPending}
      />

      {/* Alias Management Dialog */}
      {aliasLibrary && (
        <AliasList
          open={!!aliasLibrary}
          libraryName={aliasLibrary.canonical_name}
          aliases={aliases}
          onClose={() => setAliasLibrary(null)}
          onAddAlias={handleAddAlias}
          onUpdateAlias={handleUpdateAlias}
          onDeleteAlias={handleDeleteAlias}
          isLoading={
            aliasesLoading ||
            createAlias.isPending ||
            updateAlias.isPending ||
            deleteAlias.isPending
          }
        />
      )}

      {/* Section Management Dialog */}
      {sectionLibrary && (
        <SectionEditor
          open={!!sectionLibrary}
          libraryName={sectionLibrary.canonical_name}
          sections={sections}
          onClose={() => setSectionLibrary(null)}
          onAddSection={handleAddSection}
          onUpdateSection={handleUpdateSection}
          onDeleteSection={handleDeleteSection}
          isLoading={
            sectionsLoading ||
            createSection.isPending ||
            updateSection.isPending ||
            deleteSection.isPending
          }
        />
      )}
    </Box>
  );
};

// Helper component to fetch and display library stats
const LibraryStatsCard: React.FC<{
  library: Library;
  onEdit: (library: Library) => void;
  onDelete: (library: Library) => void;
  onManageAliases: (library: Library) => void;
  onManageSections: (library: Library) => void;
  onReview: (library: Library) => void;
}> = ({ library, onEdit, onDelete, onManageAliases, onManageSections, onReview }) => {
  const { data: stats } = useLibraryStats(library.id);

  return (
    <LibraryCard
      library={library}
      stats={stats}
      onEdit={onEdit}
      onDelete={onDelete}
      onManageAliases={onManageAliases}
      onManageSections={onManageSections}
      onReview={onReview}
    />
  );
};

export default LibrariesPage;
